import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatch } from 'react-router';
import { ListToolbar } from '@/components/list/ListToolbar';
import { useListParams } from '@/components/list/use-list-params';
import { cn } from '@/lib/utils';
import { usePlaceContext, type PlaceContext } from '../place/PlaceLayout';
import { apiPlace, sectionPath } from '../place/place';
import { PageHead } from '../ui/PageHead';
import { HistoryAside, ModuleListPane } from './ModuleParts';
import { useModuleText } from './module-text';
import { useModuleActs } from './use-module-acts';
import {
  useModuleHistory,
  useModules,
  type ModuleHistoryEntry,
  type ModuleState,
} from './use-modules';

export { HistoryList, MODULE_TONE, ModuleLine } from './ModuleParts';

const STATES = ['here', 'from_tenant', 'not_active'] as const;
const FILTER_KEYS = ['state'] as const;
/** What the page hands its module card (`ModuleSheet`). */
export type ModulesContext = PlaceContext & {
  readonly items: readonly ModuleState[];
  readonly loading: boolean;
  readonly history: readonly ModuleHistoryEntry[];
  readonly acts: ReturnType<typeof useModuleActs>;
};

function ModulesHead({
  titleId,
  place,
  placeName,
  q,
  states,
  list,
}: {
  readonly titleId: string;
  readonly place: PlaceContext['place'];
  readonly placeName: string;
  readonly q: string;
  readonly states: readonly (typeof STATES)[number][];
  readonly list: ReturnType<typeof useListParams>;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="people-head">
      <PageHead
        titleId={titleId}
        title={t('moduleSection.title')}
        summary={t(
          place.kind === 'organization'
            ? 'moduleSection.summaryOrg'
            : 'moduleSection.summaryTenant',
          {
            place: placeName,
          },
        )}
      />
      <ListToolbar
        label={t('moduleSection.title')}
        search={{
          label: t('moduleSection.searchLabel'),
          value: q,
          onWrite: (v) => list.set('q', v),
        }}
        fields={[
          {
            key: 'state',
            label: t('moduleSection.stateFilter'),
            options: states.map((value) => ({ value, label: t(`moduleSection.state.${value}`) })),
          },
        ]}
        active={list.active}
        onFilter={list.set}
        onClear={list.clear}
      />
    </div>
  );
}

/**
 * Moduli (FR-AD-7; the admin-area board, decision 101's list and card). Every module with its
 * state here: active here, active from the tenant (at an organization), or not active. Global and
 * tenant administrators activate and deactivate; an organization administrator reads, and the
 * server's `can` hides every button. Beside the list, the history of the acts that reach here.
 */
export function ModulesPage() {
  const context = usePlaceContext();
  const { place, detail, refreshPlace } = context;
  const pair = apiPlace(place);
  const placeKind = pair?.placeKind ?? 'organizations';
  const placeId = pair?.placeId ?? '';
  const placeName = detail.place.name;
  const { state, reload, replace } = useModules(placeKind, placeId);
  const [generation, setGeneration] = useState(0);
  const history = useModuleHistory(placeKind, placeId, generation);
  const acts = useModuleActs({
    placeKind,
    placeId,
    placeName,
    onState: replace,
    onChanged: () => setGeneration((n) => n + 1),
    onStanding: refreshPlace,
  });
  const { name } = useModuleText();
  const list = useListParams(FILTER_KEYS);
  const q = list.params.get('q') ?? '';
  const stateFilter = STATES.find((value) => value === list.params.get('state')) ?? null;
  const titleId = useId();
  const listPath = sectionPath(place, 'modules');
  const sheetOpen = useMatch(`${listPath}/:moduleId`) !== null;
  const query = list.params.size > 0 ? `?${list.params.toString()}` : '';

  const all = state.status === 'ready' ? state.items : [];
  const shown = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('it');
    return all.filter(
      (item) =>
        (stateFilter === null || item.state === stateFilter) &&
        (needle === '' || name(item.module).toLocaleLowerCase('it').includes(needle)),
    );
  }, [all, q, stateFilter, name]);
  const states = place.kind === 'organization' ? STATES : STATES.filter((s) => s !== 'from_tenant');

  const childContext: ModulesContext = {
    ...context,
    items: shown,
    loading: state.status === 'loading',
    history: history.state.items,
    acts,
  };

  return (
    <section
      aria-labelledby={titleId}
      className={cn('people-section flex flex-col gap-5', sheetOpen && 'people-section-with-sheet')}
    >
      <ModulesHead
        titleId={titleId}
        place={place}
        placeName={placeName}
        q={q}
        states={states}
        list={list}
      />
      <div
        className={cn('people-layout', sheetOpen ? 'people-layout-with-sheet' : 'modules-layout')}
      >
        <div className="people-list-pane">
          <ModuleListPane
            state={state}
            shown={shown}
            reload={reload}
            placeName={placeName}
            onClear={list.clear}
            acts={acts}
            cardHrefFor={(item) => `${listPath}/${item.module.id}${query}`}
          />
        </div>
        {sheetOpen ? (
          <Outlet context={childContext} />
        ) : (
          <HistoryAside history={history} hereId={placeId} />
        )}
      </div>
    </section>
  );
}
